/* eslint-disable prettier/prettier */
import * as React from 'react'
import { useMemo } from 'react'
import AppBar from '@mui/material/AppBar'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import CssBaseline from '@mui/material/CssBaseline'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import LogoutIcon from '@mui/icons-material/Logout'
import MenuIcon from '@mui/icons-material/Menu'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import PropTypes from 'prop-types'
import { useLocation, useNavigate } from 'react-router-dom'
import bhanzuLogoDark from '../assets/images/bhanzu-logo-white.webp'
import bhanzuLogoLight from '../assets/images/bhanzu-logo-black.svg'
import {
    guideMenuItem,
    menuItems,
    settingsMenuItem,
} from '../helpers/Constants'
import { hasAnyRole } from '../helpers/permissions'
import useAuth from '../hooks/useAuth'
import useCommsManager from '../hooks/useCommsManager'
import { useSelector } from 'react-redux'
import { useThemeMode } from '../theme/ThemeModeProvider'
import {
    ACCENT,
    APP_BAR_HEIGHT,
    ICON,
    RADIUS,
    SURFACE,
    TEXT,
} from '../theme/tokens'
import ThemeSwitch from './ThemeSwitch'
import { DOCKED_PAGE_PADDING } from './BhanzuBot/layout'

// On a wide screen the sidebar always shows in full, labels included; it has
// no collapsed state. Below md it is the header's menu instead.
const drawerWidth = 210

const NavItem = ({ item, isActive, badgeCount, onNavigate }) => (
    <ListItem disablePadding>
        <ListItemButton
            onClick={onNavigate}
            sx={{
                borderRadius: `${RADIUS.sm}px`,
                px: 1.25,
                py: 0.65,
                backgroundColor: isActive ? ACCENT.bg08 : 'transparent',
                // The active item's rule and icon are graphical marks on the
                // orange tint: ACCENT.strong keeps them at 3:1 there.
                borderLeft: isActive
                    ? `2px solid ${ACCENT.strong}`
                    : '2px solid transparent',
                '&:hover': { backgroundColor: SURFACE.hover },
            }}
        >
            <ListItemIcon
                sx={{
                    minWidth: 28,
                    '& svg': {
                        fontSize: ICON.md,
                        color: isActive ? ACCENT.strong : TEXT.muted,
                    },
                }}
            >
                {/* Only when there is something to count: MUI hides a zero
                    badge visually but keeps the "0" in the markup, which made
                    every item's accessible name "0 Home", "0 Pipelines"... */}
                {badgeCount > 0 ? (
                    <Badge badgeContent={badgeCount} color="warning">
                        {item.icon}
                    </Badge>
                ) : (
                    item.icon
                )}
            </ListItemIcon>
            <ListItemText
                primary={item.title}
                primaryTypographyProps={{
                    variant: 'body2',
                    sx: {
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? TEXT.primary : TEXT.muted,
                    },
                }}
            />
        </ListItemButton>
    </ListItem>
)

NavItem.propTypes = {
    item: PropTypes.object.isRequired,
    isActive: PropTypes.bool,
    badgeCount: PropTypes.number,
    onNavigate: PropTypes.func.isRequired,
}

Navbar.propTypes = { children: PropTypes.any }

export default function Navbar({ children }) {
    const navigate = useNavigate()
    const location = useLocation()
    const { user, logout } = useAuth()
    const { mode } = useThemeMode()
    const { canManage } = useCommsManager()
    const { templates } = useSelector((state) => state.template)
    // Below md the sidebar is a menu opened from the header: a permanent
    // 210px drawer left a phone about 190px for the page, which then
    // scrolled sideways.
    const theme = useTheme()
    const narrow = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true })
    // On a wide screen the open BhanzuBot panel gets its own column: the page
    // pads its right side so nothing sits under the panel. Narrower screens
    // keep the overlay (the panel can be minimised there).
    const wide = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true })
    const botOpen = useSelector((state) => state.assistant?.open)
    const botDocked = !!botOpen && wide
    const [menuOpen, setMenuOpen] = React.useState(false)
    const go = (link) => {
        setMenuOpen(false)
        navigate(link)
    }

    const pendingApprovals = useMemo(
        () => templates.filter((t) => t.status === 'approval_awaited').length,
        [templates]
    )

    const canSee = (permissions) => hasAnyRole(user.permissions, permissions)

    const isActiveLink = (link) =>
        location.pathname === link ||
        (link !== '/' && location.pathname.startsWith(link))

    return user && user.email ? (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backgroundColor: SURFACE.cardRaised,
                    borderBottom: `1px solid ${SURFACE.border}`,
                    height: APP_BAR_HEIGHT,
                    justifyContent: 'center',
                }}
            >
                <Toolbar
                    variant="dense"
                    sx={{ minHeight: '48px !important', px: 2 }}
                >
                    {narrow && (
                        <IconButton
                            edge="start"
                            size="small"
                            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                            aria-expanded={menuOpen}
                            aria-controls="comms-nav"
                            onClick={() => setMenuOpen(!menuOpen)}
                            sx={{ mr: 1, color: TEXT.secondary }}
                        >
                            <MenuIcon fontSize="small" />
                        </IconButton>
                    )}
                    <ButtonBase
                        onClick={() => go('/')}
                        aria-label="Go to home"
                        sx={{ mr: 2, borderRadius: `${RADIUS.xs}px` }}
                    >
                        {/* The wordmark in Deep Blue on light, white on dark. */}
                        <img
                            src={
                                mode === 'dark'
                                    ? bhanzuLogoDark
                                    : bhanzuLogoLight
                            }
                            width="80px"
                            alt="Bhanzu"
                        />
                    </ButtonBase>
                    {/* Hairline divider. A bare number is a fraction in
                        MUI's sx (width: 1 is 100%), which stretched this
                        into a full-width bar across the header. */}
                    <Box
                        aria-hidden
                        data-testid="header-divider"
                        sx={{
                            display: { xs: 'none', sm: 'block' },
                            width: '1px',
                            height: 14,
                            backgroundColor: SURFACE.border,
                            mr: 2,
                        }}
                    />
                    <Typography
                        variant="subtitle2"
                        sx={{
                            display: { xs: 'none', sm: 'block' },
                            color: TEXT.muted,
                        }}
                    >
                        Comms Platform
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <ThemeSwitch />
                </Toolbar>
            </AppBar>

            <Drawer
                id="comms-nav"
                variant={narrow ? 'temporary' : 'permanent'}
                open={narrow ? menuOpen : true}
                onClose={() => setMenuOpen(false)}
                ModalProps={{ keepMounted: true }}
                sx={{
                    width: narrow ? undefined : drawerWidth,
                    flexShrink: 0,
                    [`& .MuiDrawer-paper`]: {
                        width: drawerWidth,
                        overflowX: 'hidden',
                        boxSizing: 'border-box',
                        backgroundColor: SURFACE.card,
                        borderRight: `1px solid ${SURFACE.border}`,
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <Box sx={{ height: 48 }} />
                {/* Scrolling menu */}
                <Box sx={{ px: 1, pt: 1, flex: 1, overflowY: 'auto' }}>
                    <List
                        disablePadding
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px',
                        }}
                    >
                        {menuItems
                            .filter(
                                ({ permissions, manager }) =>
                                    canSee(permissions) &&
                                    (!manager || canManage)
                            )
                            .map((item) => {
                                if (item.type === 'section') {
                                    return (
                                        <Typography
                                            key={item.label}
                                            onClick={
                                                item.link
                                                    ? () => go(item.link)
                                                    : undefined
                                            }
                                            onKeyDown={
                                                item.link
                                                    ? (e) => {
                                                          if (
                                                              e.key ===
                                                                  'Enter' ||
                                                              e.key === ' '
                                                          ) {
                                                              e.preventDefault()
                                                              go(item.link)
                                                          }
                                                      }
                                                    : undefined
                                            }
                                            role={
                                                item.link ? 'button' : undefined
                                            }
                                            tabIndex={item.link ? 0 : undefined}
                                            sx={{
                                                px: 1.25,
                                                pt: 2,
                                                pb: 0.5,
                                                typography: 'overline',
                                                color: TEXT.faint,
                                                cursor: item.link
                                                    ? 'pointer'
                                                    : 'default',
                                                userSelect: 'none',
                                                transition: 'color 0.15s',
                                                '&:hover': item.link
                                                    ? { color: ACCENT.text }
                                                    : {},
                                                '&:focus-visible': item.link
                                                    ? {
                                                          color: ACCENT.text,
                                                          outline: 'none',
                                                      }
                                                    : {},
                                            }}
                                        >
                                            {item.label}
                                        </Typography>
                                    )
                                }
                                return (
                                    <NavItem
                                        key={item.title}
                                        item={item}
                                        isActive={isActiveLink(item.link)}
                                        badgeCount={
                                            item.badge === 'approvals'
                                                ? pendingApprovals
                                                : 0
                                        }
                                        onNavigate={() => go(item.link)}
                                    />
                                )
                            })}
                    </List>
                </Box>

                {/* Pinned bottom: settings + user */}
                <Box
                    sx={{
                        px: 1,
                        py: 1,
                        borderTop: `1px solid ${SURFACE.border}`,
                    }}
                >
                    {/* Settings for admins, then the Guide for everyone
                        signed in, also a user with no comms group. */}
                    <List disablePadding data-testid="nav-pinned">
                        {canSee(settingsMenuItem.permissions) && (
                            <NavItem
                                item={settingsMenuItem}
                                isActive={isActiveLink(settingsMenuItem.link)}
                                onNavigate={() => go(settingsMenuItem.link)}
                            />
                        )}
                        <NavItem
                            item={guideMenuItem}
                            isActive={isActiveLink(guideMenuItem.link)}
                            onNavigate={() => go(guideMenuItem.link)}
                        />
                    </List>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.25,
                            pt: 1,
                        }}
                    >
                        <Box
                            aria-hidden
                            sx={{
                                width: 22,
                                height: 22,
                                borderRadius: '50%',
                                background: ACCENT.bg15,
                                border: `1px solid ${SURFACE.borderHover}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                typography: 'overline',
                                lineHeight: 1,
                                color: TEXT.primary,
                                flexShrink: 0,
                            }}
                        >
                            {(user.email?.[0] ?? '?').toUpperCase()}
                        </Box>
                        <Typography
                            title={user.email}
                            variant="caption"
                            sx={{
                                color: TEXT.muted,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                            }}
                        >
                            {user.email}
                        </Typography>
                        <Tooltip title="Sign out">
                            <IconButton
                                size="small"
                                aria-label="Sign out"
                                onClick={logout}
                                sx={{
                                    color: TEXT.muted,
                                    '&:hover': { color: TEXT.primary },
                                }}
                            >
                                <LogoutIcon sx={{ fontSize: ICON.sm }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </Drawer>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    // A flex item's min-width is its content's width by
                    // default, so a wide table widened the whole page.
                    minWidth: 0,
                    backgroundColor: SURFACE.page,
                    minHeight: '100vh',
                }}
            >
                <Box sx={{ height: 48 }} />
                {/* 88px under the page: room for BhanzuBot's launcher, so a
                    page's last bottom-right action (Next, Send, Launch, the
                    paging bar) can be scrolled clear of it. */}
                <Box
                    data-bot-docked={botDocked ? 'true' : 'false'}
                    sx={{
                        p: '20px 24px 88px',
                        pr: botDocked ? `${DOCKED_PAGE_PADDING}px` : '24px',
                        transition: 'padding-right 200ms ease',
                    }}
                >
                    {children}
                </Box>
            </Box>
        </Box>
    ) : (
        children
    )
}
